import React, { useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { TrendingUp } from "lucide-react";
import { CHANNEL_COLOR, CHANNEL_LABEL, usd } from "./networkFmt";
import { ScopeChip } from "./NetworkHeadline";

const SERIES = ["prod", "stage", "dev", "other"];

function Tip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    const total = payload.reduce((s, p) => s + Number(p.value || 0), 0);
    return (
        <div className="bg-[#111] border border-white/15 rounded-md px-3 py-2 text-[11px] font-mono">
            <div className="text-white/60 mb-1">{label}</div>
            {payload.filter((p) => p.value).map((p) => (
                <div key={p.dataKey} className="flex justify-between gap-4"><span style={{ color: p.color }}>{CHANNEL_LABEL[p.dataKey]}</span><span>{usd(p.value)}</span></div>
            ))}
            <div className="flex justify-between gap-4 border-t border-white/10 mt-1 pt-1"><span>Total</span><span>{usd(total)}</span></div>
        </div>
    );
}

/** Spend over time (daily or monthly), stacked by channel. */
export default function NetworkSpendChart({ data }) {
    const [mode, setMode] = useState("day");
    const points = mode === "day" ? data.by_day : data.by_month;
    const tab = (k, label) => (
        <button onClick={() => setMode(k)} data-testid={`network-chart-${k}`}
            className={`px-2.5 py-1 rounded text-[11px] transition-colors ${mode === k ? "bg-white/10 text-white" : "text-white/45 hover:text-white"}`}>{label}</button>
    );
    return (
        <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-4" data-testid="network-spend-chart">
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <TrendingUp className="w-3.5 h-3.5 text-[#7C3AED]" /><div className="font-display text-sm">Spend over time</div>
                <ScopeChip data={data} testId="network-chart-scope" />
                <div className="ml-auto flex items-center gap-1">{tab("day", "Daily")}{tab("month", "Monthly")}</div>
            </div>
            {points.length === 0 ? <div className="text-xs text-white/40 py-10 text-center">No spend in range.</div> : (
                <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={points} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                            <XAxis dataKey="key" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={16} />
                            <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} tickLine={false} axisLine={false} width={52} tickFormatter={(v) => usd(v, 0)} />
                            <Tooltip content={<Tip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                            {SERIES.map((s) => <Bar key={s} dataKey={s} stackId="spend" fill={CHANNEL_COLOR[s]} radius={s === "other" ? [2, 2, 0, 0] : 0} />)}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
            <div className="flex flex-wrap gap-3 mt-2">
                {SERIES.slice(0, 3).map((s) => (
                    <span key={s} className="flex items-center gap-1.5 text-[10px] text-white/50"><span className="w-2 h-2 rounded-sm" style={{ background: CHANNEL_COLOR[s] }} />{CHANNEL_LABEL[s]}</span>
                ))}
            </div>
        </div>
    );
}
